import React, { useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { TaskSection } from '../interfaces/Task';
import { useTranslation } from 'react-i18next';
import ContextMenu, { ContextMenuItem } from './ContextMenu';

interface FolderTreeProps {
    sections: TaskSection[];
    selectedFolderId: string | null;
    expandedFolders: string[];
    onSelectFolder: (folderId: string | null) => void;
    onToggleExpand: (folderId: string) => void;
    onDropIntoFolder: (itemId: string, itemType: 'section' | 'task', targetFolderId: string | null) => void;
    onEditFolder: (section: TaskSection) => void;
    onDeleteFolder: (sectionId: string) => void;
    onCreateFolder: (parentId: string | null) => void;
    onCreateTask: (sectionId: string | null) => void;
}

interface TreeItemProps {
    section: TaskSection;
    level: number;
    selectedFolderId: string | null;
    expandedFolders: string[];
    onSelectFolder: (folderId: string | null) => void;
    onToggleExpand: (folderId: string) => void;
    onDropIntoFolder: (itemId: string, itemType: 'section' | 'task', targetFolderId: string | null) => void;
    onEditFolder: (section: TaskSection) => void;
    onDeleteFolder: (sectionId: string) => void;
    onCreateFolder: (parentId: string | null) => void;
    onCreateTask: (sectionId: string | null) => void;
}

function TreeItem(props: TreeItemProps) {
    const { t } = useTranslation();
    const { section, level, selectedFolderId, expandedFolders, onSelectFolder, onToggleExpand, onDropIntoFolder, onEditFolder, onDeleteFolder, onCreateFolder, onCreateTask } = props;
    const hasChildren = section.children && section.children.length > 0;
    const isExpanded = expandedFolders.includes(section._id);
    const isSelected = selectedFolderId === section._id;
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

    // Drag functionality
    const [{ isDragging }, drag] = useDrag(() => ({
        type: 'section',
        item: {
            id: section._id,
            sectionId: section._id,
            type: 'section' as const,
            section: section
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    }), [section._id]);

    // Drop functionality
    const [{ isOver, canDrop }, drop] = useDrop(() => ({
        accept: ['section', 'task'],
        drop: (item: { id: string; type: 'section' | 'task'; sectionId?: string; taskId?: string; selectedItems?: { id: string; type: 'section' | 'task' }[] }, monitor) => {
            if (monitor.didDrop()) return;

            // Handle multiple items
            if (item.selectedItems && item.selectedItems.length > 1) {
                item.selectedItems.forEach(si => {
                    // Don't drop a folder into itself
                    if (!(si.id === section._id && si.type === 'section')) {
                        onDropIntoFolder(si.id, si.type, section._id);
                    }
                });
            } else {
                const itemId = item.type === 'section' ? item.sectionId || item.id : item.taskId || item.id;
                if (itemId) {
                    onDropIntoFolder(itemId, item.type, section._id);
                }
            }
        },
        canDrop: (item: { id: string; type: 'section' | 'task'; sectionId?: string; selectedItems?: { id: string; type: 'section' | 'task' }[] }) => {
            if (item.type === 'section') {
                const sectionId = item.sectionId || item.id;
                if (sectionId === section._id) return false;
            }
            // Check if this folder is in the selection being dragged
            if (item.selectedItems) {
                const isTargetInSelection = item.selectedItems.some(
                    si => si.id === section._id && si.type === 'section'
                );
                if (isTargetInSelection) return false;
            }
            return true;
        },
        collect: (monitor) => ({
            isOver: monitor.isOver({ shallow: true }),
            canDrop: monitor.canDrop(),
        }),
    }), [section._id, onDropIntoFolder]);

    // Combine drag and drop refs
    const combinedRef = (node: HTMLDivElement | null) => {
        drag(node);
        drop(node);
    };

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onSelectFolder(section._id);
    };

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        onToggleExpand(section._id);
    };

    const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setContextMenu({ x: e.clientX, y: e.clientY });
    };

    const contextMenuItems: ContextMenuItem[] = [
        {
            label: t("Open"),
            icon: 'bi-folder2-open',
            onClick: () => onSelectFolder(section._id)
        },
        {
            label: t("Edit"),
            icon: 'bi-pencil',
            onClick: () => onEditFolder(section)
        },
        {
            label: t("New subfolder"),
            icon: 'bi-folder-plus',
            onClick: () => onCreateFolder(section._id),
            divider: true
        },
        {
            label: t("New task"),
            icon: 'bi-file-earmark-plus',
            onClick: () => onCreateTask(section._id)
        },
        {
            label: t("Delete"),
            icon: 'bi-trash',
            onClick: () => onDeleteFolder(section._id),
            danger: true,
            divider: true
        }
    ];

    return (
        <div className="tree-item-container">
            <div
                ref={combinedRef}
                className={`tree-item ${isSelected ? 'selected' : ''} ${isOver && canDrop ? 'drop-target' : ''} ${isDragging ? 'dragging' : ''}`}
                style={{ paddingLeft: `${12 + level * 16}px` }}
                onClick={handleClick}
                onContextMenu={handleContextMenu}
            >
                <span
                    className={`expand-icon ${hasChildren ? 'has-children' : ''}`}
                    onClick={hasChildren ? handleToggle : undefined}
                >
                    {hasChildren ? (isExpanded ? '▼' : '▶') : ''}
                </span>
                <i className={`bi ${isExpanded && hasChildren ? 'bi-folder2-open' : 'bi-folder2'} folder-icon`}></i>
                <span className="folder-name">{section.name}</span>
            </div>
            {hasChildren && isExpanded && (
                <div className="tree-children">
                    {section.children!.sort((a, b) => a.weight - b.weight).map(child => (
                        <TreeItem
                            key={child._id}
                            section={child}
                            level={level + 1}
                            selectedFolderId={selectedFolderId}
                            expandedFolders={expandedFolders}
                            onSelectFolder={onSelectFolder}
                            onToggleExpand={onToggleExpand}
                            onDropIntoFolder={onDropIntoFolder}
                            onEditFolder={onEditFolder}
                            onDeleteFolder={onDeleteFolder}
                            onCreateFolder={onCreateFolder}
                            onCreateTask={onCreateTask}
                        />
                    ))}
                </div>
            )}
            {contextMenu && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    items={contextMenuItems}
                    onClose={() => setContextMenu(null)}
                />
            )}
        </div>
    );
}

export default function FolderTree(props: FolderTreeProps) {
    const { t } = useTranslation();
    const { sections, selectedFolderId, expandedFolders, onSelectFolder, onToggleExpand, onDropIntoFolder, onEditFolder, onDeleteFolder, onCreateFolder, onCreateTask } = props;
    const isRootSelected = selectedFolderId === null;
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

    const [{ isOver, canDrop }, drop] = useDrop(() => ({
        accept: ['section', 'task'],
        drop: (item: { id: string; type: 'section' | 'task'; sectionId?: string; taskId?: string; selectedItems?: { id: string; type: 'section' | 'task' }[] }, monitor) => {
            if (monitor.didDrop()) return;

            // Handle multiple items
            if (item.selectedItems && item.selectedItems.length > 1) {
                item.selectedItems.forEach(si => {
                    onDropIntoFolder(si.id, si.type, null);
                });
            } else {
                const itemId = item.type === 'section' ? item.sectionId || item.id : item.taskId || item.id;
                if (itemId) {
                    onDropIntoFolder(itemId, item.type, null);
                }
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver({ shallow: true }),
            canDrop: monitor.canDrop(),
        }),
    }), [onDropIntoFolder]);

    const handleRootContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setContextMenu({ x: e.clientX, y: e.clientY });
    };

    const rootContextMenuItems: ContextMenuItem[] = [
        {
            label: t("New folder"),
            icon: 'bi-folder-plus',
            onClick: () => onCreateFolder(null)
        },
        {
            label: t("New task"),
            icon: 'bi-file-earmark-plus',
            onClick: () => onCreateTask(null)
        }
    ];

    return (
        <div className="folder-tree">
            {/* Root folder */}
            <div
                ref={drop as any}
                className={`tree-item root-item ${isRootSelected ? 'selected' : ''} ${isOver && canDrop ? 'drop-target' : ''}`}
                onClick={() => onSelectFolder(null)}
                onContextMenu={handleRootContextMenu}
            >
                <span className="expand-icon"></span>
                <i className="bi bi-house-door folder-icon"></i>
                <span className="folder-name">{t("All Tasks")}</span>
            </div>

            {/* Section tree */}
            <div className="tree-sections">
                {sections
                    .filter(s => !s.parentId)
                    .sort((a, b) => a.weight - b.weight)
                    .map(section => (
                        <TreeItem
                            key={section._id}
                            section={section}
                            level={0}
                            selectedFolderId={selectedFolderId}
                            expandedFolders={expandedFolders}
                            onSelectFolder={onSelectFolder}
                            onToggleExpand={onToggleExpand}
                            onDropIntoFolder={onDropIntoFolder}
                            onEditFolder={onEditFolder}
                            onDeleteFolder={onDeleteFolder}
                            onCreateFolder={onCreateFolder}
                            onCreateTask={onCreateTask}
                        />
                    ))}
            </div>

            {contextMenu && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    items={rootContextMenuItems}
                    onClose={() => setContextMenu(null)}
                />
            )}
        </div>
    );
}
